import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { BrandDetail } from "./types";
import { resolveRegion } from "./regionResolve";

// Persist the selected market PER BRAND so it survives navigation that doesn't
// carry ?region= (sidebar nav, plain Links). Without this the market reverts to
// the brand's primary the moment you move pages — the "I picked US on Flows,
// Campaigns showed UK" bug. Resolution order is URL param → persisted → primary,
// so deep-links still win and a stale/inactive persisted market is ignored.
const REGION_STORE_PREFIX = "riq.region.";
function readStoredRegion(slug: string | undefined): string | null {
  if (!slug) return null;
  try {
    return localStorage.getItem(REGION_STORE_PREFIX + slug);
  } catch {
    return null; // storage disabled (private mode) — URL param still works
  }
}
function writeStoredRegion(slug: string | undefined, region: string | null): void {
  if (!slug) return;
  try {
    if (region) localStorage.setItem(REGION_STORE_PREFIX + slug, region);
    else localStorage.removeItem(REGION_STORE_PREFIX + slug);
  } catch {
    /* storage unavailable — non-fatal; URL param still scopes this page */
  }
}

/**
 * Region selection lives in the URL search param `?region=US` so it's
 * deep-linkable, browser-back works, and copy-pasted URLs preserve context.
 *
 * DEFAULT = the brand's PRIMARY market (its largest market by revenue, from
 * `brand.primary_region`). A multi-region brand opens scoped to that single
 * market — NOT an all-markets aggregate. "All markets" is the explicit opt-in
 * and is represented by the `/overview` route (the read-only roll-up), not by a
 * region value here. `?region=all` still resolves to null for deep-link safety.
 *
 * Resolved region:
 *   - `?region=XX` matching an ACTIVE connected_account region → that market.
 *   - `?region=all` → null ("All markets" — only the Overview reads this).
 *   - no/invalid param → the primary market (largest), or the first active
 *     region as a fallback, or null when the brand has no active region.
 */
export function useRegion(brand: BrandDetail | undefined | null) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlRegion = searchParams.get("region");
  const slug = brand?.slug;
  const stored = readStoredRegion(slug);

  const activeRegions = useMemo(() => {
    if (!brand) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const a of brand.connected_accounts) {
      if (a.auth_status !== "active") continue;
      if (!a.region) continue;
      const r = a.region.toUpperCase();
      if (!seen.has(r)) {
        seen.add(r);
        out.push(r);
      }
    }
    return out.sort();
  }, [brand]);

  // The brand's largest market (home market). Falls back to the first active
  // region when the backend hasn't resolved one (e.g. no CH revenue yet).
  const primary = useMemo<string | null>(() => {
    const p = (brand?.primary_region || "").toUpperCase();
    if (p && activeRegions.includes(p)) return p;
    return activeRegions[0] ?? null;
  }, [brand, activeRegions]);

  const region = useMemo<string | null>(
    () => resolveRegion(urlRegion, stored, activeRegions, primary),
    [urlRegion, stored, activeRegions, primary],
  );

  /** Set a specific market, or clear (null) to fall back to the primary
   *  default. "All markets" is NOT set here — it's the `/overview` route. */
  function setRegion(next: string | null) {
    const norm = next ? next.toUpperCase() : null;
    // Persist per-brand so the choice survives navigation that drops ?region=.
    writeStoredRegion(slug, norm);
    const params = new URLSearchParams(searchParams);
    if (norm) {
      params.set("region", norm);
    } else {
      params.delete("region");
    }
    setSearchParams(params, { replace: false });
  }

  return {
    region,
    /** The largest/home market the brand defaults to. */
    primary,
    setRegion,
    activeRegions,
    /** True when picker should render: brand has 2+ regions wired */
    multiRegion: activeRegions.length >= 2,
  };
}

/** Query-string fragment that preserves the selected market across navigation
 *  (e.g. an entity deep-link → detail page). Returns "" for the All-markets /
 *  default case so URLs stay clean. Use on every `<Link>` into a region-scoped
 *  detail page (campaigns, flows) so the detail page's useRegion resolves the
 *  entity's market instead of falling back to the brand's primary. */
export function regionQuery(region: string | null | undefined): string {
  return region ? `?region=${encodeURIComponent(region)}` : "";
}

/** Hook form: preserve the CURRENT URL's market on a deep-link, with no prop
 *  threading. Reads the raw `?region=` param — so on the primary/All-markets
 *  view (no param) it returns "" and the detail page's useRegion falls back to
 *  primary; when the user has switched markets it carries that choice through.
 *  Use at any `<Link>` into a region-scoped detail page. */
export function useRegionQuery(): string {
  const [params] = useSearchParams();
  return regionQuery(params.get("region"));
}
